import { CURRENT_TAX_YEAR } from '@/constants';
import { addOrEditItem, deleteItem, getItems } from '@/store/api';

/**
 * Gift Tax Questionnaire store — sibling of store/tax_season.js.
 *
 * The questionnaire record is created by Ops enrollment (no create endpoint);
 * the app fetches it, transitions its status, and PATCHes explicit answer
 * columns. Repeatable gift rows and the questionnaire's files (basis
 * documentation / prior 709 / 706) are managed via nested endpoints.
 */
export default {
    namespaced: true,
    state: {
        currentGiftTax: null,
        giftsById: {},
    },
    mutations: {
        updateCurrentGiftTax(state, questionnaire) {
            state.currentGiftTax = questionnaire;
            const indexed = {};
            (questionnaire?.gifts || []).forEach(gift => {
                indexed[gift.id] = gift;
            });
            state.giftsById = indexed;
        },
        clearCurrentGiftTax(state) {
            state.currentGiftTax = null;
            state.giftsById = {};
        },
    },
    getters: {
        currentGiftTax: state => state.currentGiftTax,
        gifts: state => state.currentGiftTax?.gifts || [],
        formDocuments: state => state.currentGiftTax?.documents || [],
        status: state => state.currentGiftTax?.status || null,
        isStarted: state => !!state.currentGiftTax && state.currentGiftTax.status !== 'not_started',
        isSubmitted: state => state.currentGiftTax?.status === 'submitted',
        // Editing is allowed both before and after submission (Google-Forms parity).
        isEditable: state => !!state.currentGiftTax,
    },
    actions: {
        // --- Questionnaire (singleton per user + tax_year) ---
        getGiftTax: getItems('gift-tax/'),
        updateGiftTax: addOrEditItem('gift-tax/'),

        // --- Gift rows (gift-tax/{tax_year}/gifts/) ---
        getGifts: getItems('gifts/', 'gift-tax/'),
        createGift: addOrEditItem('gifts/', 'gift-tax/'),
        updateGift: addOrEditItem('gifts/', 'gift-tax/'),
        deleteGift: deleteItem('gifts/', 'gift-tax/'),

        // --- Questionnaire files (basis documentation / prior 709 / 706) ---
        async createFormDocument({ dispatch, rootGetters }, { tax_year, user_id, ...payload }) {
            const path = `gift-tax/${tax_year}/documents/`;
            return await addOrEditItem(path)({ dispatch, rootGetters }, { ...payload, ...(user_id && { user_id }) });
        },
        async deleteFormDocument({ dispatch, rootGetters }, { tax_year, doc_id, user_id }) {
            const path = `gift-tax/${tax_year}/documents/`;
            return await deleteItem(path)({ dispatch, rootGetters }, { id: doc_id, ...(user_id && { user_id }) });
        },

        // --- Business logic ---
        async fetchGiftTax({ commit, dispatch }, { tax_year = CURRENT_TAX_YEAR } = {}) {
            const data = await dispatch('getGiftTax', { id: tax_year });
            commit(data ? 'updateCurrentGiftTax' : 'clearCurrentGiftTax', data || null);
            return data;
        },

        // Save a section's answers; transition to in_progress on first edit.
        async saveAnswers({ commit, dispatch, getters }, { tax_year = CURRENT_TAX_YEAR, ...answers }) {
            const updates = { id: tax_year, ...answers };
            if (getters.status === 'not_started') updates.status = 'in_progress';
            const data = await dispatch('updateGiftTax', updates);
            if (data) commit('updateCurrentGiftTax', data);
            return data;
        },

        async submitQuestionnaire({ commit, dispatch }, { tax_year = CURRENT_TAX_YEAR } = {}) {
            const data = await dispatch('updateGiftTax', { id: tax_year, status: 'submitted' });
            if (data) commit('updateCurrentGiftTax', data);
            return data;
        },

        // Gift-row helpers that refresh the questionnaire (nested gifts + docs) after mutating.
        async addGift({ dispatch }, { tax_year = CURRENT_TAX_YEAR, ...payload } = {}) {
            const gift = await dispatch('createGift', { additional_id: tax_year, ...payload });
            await dispatch('fetchGiftTax', { tax_year });
            return gift;
        },
        async editGift({ dispatch }, { tax_year = CURRENT_TAX_YEAR, gift_id, ...payload }) {
            const gift = await dispatch('updateGift', { additional_id: tax_year, id: gift_id, ...payload });
            await dispatch('fetchGiftTax', { tax_year });
            return gift;
        },
        async removeGift({ dispatch }, { tax_year = CURRENT_TAX_YEAR, gift_id }) {
            await dispatch('deleteGift', { additional_id: tax_year, id: gift_id });
            await dispatch('fetchGiftTax', { tax_year });
        },

        // ============================================
        // ADVISOR-SPECIFIC ACTIONS (act on a client by passing client_id)
        // ============================================
        async fetchAdvisorClientGiftTax({ commit, dispatch }, { client_id, tax_year = CURRENT_TAX_YEAR }) {
            const data = await dispatch('getGiftTax', { id: tax_year, user_id: client_id });
            commit(data ? 'updateCurrentGiftTax' : 'clearCurrentGiftTax', data || null);
            return data;
        },
        async fetchAdvisorClientGiftTaxStatus({ dispatch }, { client_id, tax_year = CURRENT_TAX_YEAR }) {
            // getItems swallows errors (incl. the 404 for a client who was never
            // enrolled) and resolves `false`; normalize that to null so callers can
            // tell "no questionnaire" from a real not_started record.
            const data = await dispatch('getGiftTax', { id: tax_year, user_id: client_id });
            return data || null;
        },
        async saveAdvisorClientAnswers(
            { commit, dispatch, getters },
            { client_id, tax_year = CURRENT_TAX_YEAR, ...answers },
        ) {
            const updates = { id: tax_year, user_id: client_id, ...answers };
            if (getters.status === 'not_started') updates.status = 'in_progress';
            const data = await dispatch('updateGiftTax', updates);
            if (data) commit('updateCurrentGiftTax', data);
            return data;
        },
        async submitAdvisorClientQuestionnaire({ commit, dispatch }, { client_id, tax_year = CURRENT_TAX_YEAR }) {
            const data = await dispatch('updateGiftTax', { id: tax_year, user_id: client_id, status: 'submitted' });
            if (data) commit('updateCurrentGiftTax', data);
            return data;
        },
        async addAdvisorClientGift({ dispatch }, { client_id, tax_year = CURRENT_TAX_YEAR, ...payload }) {
            const gift = await dispatch('createGift', { additional_id: tax_year, user_id: client_id, ...payload });
            await dispatch('fetchAdvisorClientGiftTax', { client_id, tax_year });
            return gift;
        },
        async editAdvisorClientGift({ dispatch }, { client_id, tax_year = CURRENT_TAX_YEAR, gift_id, ...payload }) {
            const gift = await dispatch('updateGift', {
                additional_id: tax_year,
                id: gift_id,
                user_id: client_id,
                ...payload,
            });
            await dispatch('fetchAdvisorClientGiftTax', { client_id, tax_year });
            return gift;
        },
        async removeAdvisorClientGift({ dispatch }, { client_id, tax_year = CURRENT_TAX_YEAR, gift_id }) {
            await dispatch('deleteGift', { additional_id: tax_year, id: gift_id, user_id: client_id });
            await dispatch('fetchAdvisorClientGiftTax', { client_id, tax_year });
        },
    },
};
